import {CompanyEntity, CustomFieldDefDataType, EntityType, toCustomFieldDefDataType} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Modal, ModalProps} from "@mantine/core";
import {useCallback} from "react";
import {CustomFieldDefFormProvider, useCustomFieldDefForm} from "./useCustomFieldDefForm";
import {CustomFieldDefFormFields} from "./CustomFieldDefFormFields";

export interface CustomFieldDefFormProps {
  options?: CustomFieldDefFormOptions;
  defaultValues?: Partial<CustomFieldDefFormValues>;
  onSubmit?: (values: CustomFieldDefFormValues) => void;
  submitting?: boolean;
}

export interface CustomFieldDefFormOptions {
  regionId?: string;
  companySelectDisabled?: boolean
}

export interface CustomFieldDefFormValues {
  company: CompanyEntity | string
  objectType: EntityType | string
  code: string
  dataType: CustomFieldDefDataType
  refObjectType?: EntityType | string
  required?: boolean
  label: string
}

export interface CustomFieldDefFormDefaultValues extends Partial<CustomFieldDefFormValues> {

}

export interface CustomFieldDefFormRawValues {
  company?: string
  objectType?: string
  code?: string
  dataType?: string
  refObjectType?: string
  required?: boolean
  label?: string
}

export type CustomFieldDefFormType = UseFormReturnType<CustomFieldDefFormRawValues, (values: CustomFieldDefFormRawValues) => CustomFieldDefFormRawValues>

export const CustomFieldDefForm = (props: CustomFieldDefFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useCustomFieldDefForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        company: values.company,
        objectType: values.objectType,
        code: values.code,
        dataType: toCustomFieldDefDataType(values.dataType!),
        refObjectType: values.refObjectType,
        required: values.required,
        label: values.label,
      });
    }
  }, [form, onSubmit]);

  return (
      <CustomFieldDefFormProvider form={form}>
        <form onSubmit={form.onSubmit(onFormSubmit)}>
          <CustomFieldDefFormFields options={options}/>
          <Divider my="sm" variant="dotted"/>
          <Button type="submit" loading={submitting} fullWidth mt="xl">
            {t("common.action.submit", {ns: "common"})}
          </Button>
        </form>
      </CustomFieldDefFormProvider>
  );
};

interface CustomFieldDefFormModalProps extends CustomFieldDefFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const CustomFieldDefFormModal = (props: CustomFieldDefFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
      <Modal {...rest}>
        <CustomFieldDefForm
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            submitting={submitting}/>
      </Modal>
  );
};

